import type { Vec2 } from "../game/math";
import type { CosmeticId, ThemePreference } from "../progress/schema";
import {
  cosmeticBodyLayers,
  cosmeticHeadShapes,
  cosmeticMarkShapes,
  cosmeticPathData,
  type CosmeticShape,
} from "../render/cosmetic-art";
import { buildCosmeticGeometry } from "../render/cosmetic-geometry";
import { getCosmeticStyle } from "../theme/theme";

export type CosmeticPreviewVariant = "hero" | "card" | "detail";

type Cubic = readonly [Vec2, Vec2, Vec2, Vec2];

const sampleCurves = (curves: readonly Cubic[]): readonly Vec2[] =>
  curves.flatMap(([start, first, second, end], curveIndex) =>
    Array.from({ length: 33 }, (_, index) => {
      const t = index / 32;
      const inverse = 1 - t;
      return {
        x:
          inverse ** 3 * start.x +
          3 * inverse ** 2 * t * first.x +
          3 * inverse * t ** 2 * second.x +
          t ** 3 * end.x,
        y:
          inverse ** 3 * start.y +
          3 * inverse ** 2 * t * first.y +
          3 * inverse * t ** 2 * second.y +
          t ** 3 * end.y,
      };
    }).filter((_, index) => curveIndex === 0 || index > 0),
  );

const heroTrail = sampleCurves([
  [
    { x: 142, y: 342 },
    { x: 199, y: 391 },
    { x: 306, y: 370 },
    { x: 341, y: 311 },
  ],
  [
    { x: 341, y: 311 },
    { x: 390, y: 228 },
    { x: 301, y: 202 },
    { x: 227, y: 225 },
  ],
  [
    { x: 227, y: 225 },
    { x: 103, y: 264 },
    { x: 105, y: 140 },
    { x: 191, y: 116 },
  ],
  [
    { x: 191, y: 116 },
    { x: 289, y: 88 },
    { x: 320, y: 199 },
    { x: 415, y: 173 },
  ],
  [
    { x: 415, y: 173 },
    { x: 463, y: 160 },
    { x: 483, y: 134 },
    { x: 477, y: 97 },
  ],
]);
const cardTrail = sampleCurves([
  [
    { x: 31, y: 93 },
    { x: 69, y: 129 },
    { x: 90, y: 30 },
    { x: 134, y: 69 },
  ],
  [
    { x: 134, y: 69 },
    { x: 176, y: 112 },
    { x: 222, y: 109 },
    { x: 245, y: 54 },
  ],
]);

const pathData = (points: readonly Vec2[]): string =>
  points
    .map((point, index) => `${index === 0 ? "M" : "L"}${point.x} ${point.y}`)
    .join(" ");

const degrees = (radians: number): number => (radians * 180) / Math.PI;

function Shape({ shape }: { readonly shape: CosmeticShape }) {
  const presentation = {
    fill: shape.fill ?? "none",
    stroke: shape.stroke,
    strokeWidth: shape.strokeWidth,
    opacity: shape.opacity,
    "data-art-role": shape.role,
  };
  return shape.kind === "ellipse" ? (
    <ellipse
      cx={shape.center.x}
      cy={shape.center.y}
      rx={shape.radiusX}
      ry={shape.radiusY}
      {...presentation}
    />
  ) : (
    <path d={cosmeticPathData(shape.path)} {...presentation} />
  );
}

export function CosmeticPreview({
  cosmetic,
  theme,
  variant = "card",
}: {
  readonly cosmetic: CosmeticId;
  readonly theme: ThemePreference;
  readonly variant?: CosmeticPreviewVariant;
}) {
  const style = getCosmeticStyle(theme, cosmetic);
  const large = variant !== "card";
  const width = large ? 38 : 23;
  const trail = large ? heroTrail : cardTrail;
  const geometry = buildCosmeticGeometry(
    trail,
    width,
    style.pattern,
    "quality",
  );
  const bodyPath = pathData(trail);
  const head = geometry.head;
  const hasSpine =
    style.pattern === "signal" ||
    style.pattern === "tide" ||
    style.pattern === "circuit";

  return (
    <div
      className={`cosmetic-preview cosmetic-preview--${variant}`}
      data-pattern={style.pattern}
      data-material="smooth"
      data-segmented="false"
      data-head-mark={head.mark}
      data-surface-detail={geometry.surfaceDetail}
      aria-hidden="true"
    >
      <svg
        viewBox={large ? "0 0 620 440" : "0 0 280 150"}
        preserveAspectRatio="xMidYMid meet"
        focusable="false"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {cosmeticBodyLayers(style, "quality").map((layer, index) => (
          <path
            key={index}
            data-art-role="body"
            d={bodyPath}
            stroke={layer.color}
            strokeWidth={layer.width * width}
            strokeOpacity={layer.opacity}
            transform={`translate(${layer.offset.x * width} ${layer.offset.y * width})`}
          />
        ))}
        {geometry.surfaceDetail === "tide-ripple"
          ? geometry.samples
              .filter((sample) => sample.index % 2 === 0)
              .map((sample) => (
                <path
                  key={`ripple-${sample.index}`}
                  data-surface-detail="tide-ripple"
                  transform={`translate(${sample.center.x} ${sample.center.y}) rotate(${degrees(sample.angle)}) scale(${sample.radius})`}
                  d="M -.38 -.65 Q .25 0 -.38 .65"
                  stroke={style.facet}
                  strokeWidth=".09"
                  strokeOpacity=".62"
                />
              ))
          : null}
        {hasSpine ? (
          <g
            strokeDasharray={style.dash.map((value) => value * width).join(" ")}
          >
            <path
              d={bodyPath}
              stroke={style.motifOutline}
              strokeWidth={width * 0.12}
              strokeOpacity=".7"
            />
            <path
              d={bodyPath}
              stroke={style.accent}
              strokeWidth={width * 0.045}
              strokeOpacity=".9"
            />
          </g>
        ) : null}
        {geometry.marks.map((mark, index) => (
          <g
            key={`${mark.kind}-${index}`}
            transform={`translate(${mark.center.x} ${mark.center.y}) rotate(${degrees(mark.angle)}) scale(${mark.radius})`}
          >
            {cosmeticMarkShapes(mark, style).map((shape, shapeIndex) => (
              <Shape key={shapeIndex} shape={shape} />
            ))}
          </g>
        ))}
        <g
          data-head-detail={head.mark}
          transform={`translate(${head.center.x} ${head.center.y}) rotate(${degrees(head.angle)}) scale(${head.radius})`}
        >
          {cosmeticHeadShapes(style).map((shape, index) => (
            <Shape key={index} shape={shape} />
          ))}
        </g>
      </svg>
    </div>
  );
}
